
import { LmvMatrix4 as Matrix4 } from '../../wgs/scene/LmvMatrix4';
import { LmvBox3 as Box3 } from '../../wgs/scene/LmvBox3';
import { logger } from "../../logger/Logger";
import { TextureLoader } from "./TextureLoader";
import { BVH, NodeArray } from "../../wgs/scene/BVHBuilder";
import { MeshFlags } from '../../wgs/scene/MeshFlags';
import { PropDbLoader } from "./PropDbLoader";
import { createWorker } from "./WorkerCreator";
import { initLoadContext } from "../net/endpoints";
import { pathToURL, ViewingService } from "../net/Xhr";
import { OtgPackage, FLUENT_URN_PREFIX, DS_OTG_CDN_PREFIX } from "../lmvtk/otg/Otg";
import { FileLoaderManager } from "../../application/FileLoaderManager";
import { Model } from "../../application/Model";
import { BubbleNode } from "../../application/bubble";
import * as et from "../../application/EventTypes";
import { LocalStorage } from '../../application/LocalStorage.js';
import { ProgressState } from '../../application/ProgressState';
import { isMobileDevice } from "../../compat";
import { MESH_RECEIVE_EVENT, MESH_FAILED_EVENT, MATERIAL_RECEIVE_EVENT, MATERIAL_FAILED_EVENT } from "./OtgResourceCache";
import { binToPackedString, bin16ToPackedString, packedToBin, digest } from '../lmvtk/otg/HashStrings';
import { BVH_CACHING, USE_OUT_OF_CORE_TILE_MANAGER, USE_MULTI_MATERIAL_RENDER_CALLS, USE_SERVER_SIDE_BVH, INVALID_GEOM_ID, USE_GEOMETRY_OOC } from "../../wgs/globals";
import { OtgFragInfo } from "../lmvtk/otg/OtgFragInfo";
//import { createWireframe } from "../../wgs/scene/DeriveTopology;
import { SelectiveLoadingController } from '../selective/SelectiveLoadingController';
import { computeOutOfCoreBVH } from '../../wgs/scene/out-of-core-tile-manager/BvhCompute.js';
import { InputStream } from '../lmvtk/common/InputStream';
import { ROOT_LOADED_PROGRESS } from "./constants.js";
/** @import { Viewer3DImpl } from "../../application/Viewer3DImpl"} */

var WORKER_LOAD_OTG_BVH = "LOAD_OTG_BVH";
const av = Autodesk.Viewing;
const avp = Autodesk.Viewing.Private;

/**
 * @constructor
 * @param {Viewer3DImpl} parent
 */
export function OtgLoader(parent) {
  /** @type {Viewer3DImpl} */
  this.viewer3DImpl = parent;

  this.loading = false;
  this.tmpMatrix = new Matrix4();
  this.tmpBox = new Box3();

  this.logger = logger;
  this.loadTime = 0;

  this.pendingMaterials = new Map();

  this.pendingMeshes = new Array();

  this.hashToGeomId = new Map();

  this.pendingRequests = 0;

  this.operationsDone = 0;
  this.missingExtraMaterials = new Set();

  this.notifiesFirstPixel = true;

  this._selectiveLoadingController = new SelectiveLoadingController(OtgLoader.prototype.evaluate.bind(this));

  this._lineageUrn = null;

  this.loadFromCache = false;

  // Unique identifier for the HLOD cache (containing configuration parameters
  // that can influence the computed BVH)
  this.hlodCacheIdentifier = undefined;

  // Keep track of processed chunkIds to avoid processing the same chunk multiple times
  this.processedChunkIds = new Set();

  // We need to keep track of failed materials to know when we can enable a fragment that depends on it.
  this.failedMaterials = new Set();
}


OtgLoader.prototype.dtor = function () {
  // Cancel all potential process on loading a file.

  // 2. load model root (aka. svf) can be cancelled.
  //
  if (this.bvhWorker) {
    this.bvhWorker.terminate();
    this.bvhWorker = null;
    logger.debug("SVF loader dtor: on svf worker.");
  }


  if (this.svf) {

    if (!this.svf.loadDone)
    console.log("stopping load before it was complete");

    this.svf.abort();

    if (this.svf.propDbLoader) {
      this.svf.propDbLoader.dtor();
      this.svf.propDbLoader = null;
    }
  }


  // 5. Cancel all running requests in shared geometry worker
  //
  if (this.viewer3DImpl.geomCache() && this.model) {
    if (this.loading)
    this.viewer3DImpl.geomCache().cancelRequests(this.svf.geomMetadata.hashToIndex.keys());

    this.pendingRequests = 0;
    this.removeMeshReceiveListener();
  }

  // and clear metadata.
  this.viewer3DImpl = null;
  this.model = null;
  this.processedChunkIds = null;

  if (this.options) {
    // Clean up any function references that might create circular dependencies
    this.options.addViews = null;
    this.options.hydrateViews = null;
  }

  this.svf = null;
  this.logger = null;
  this.tmpMatrix = null;

  this.loading = false;
  this.loadTime = 0;
};

OtgLoader.prototype.addMeshReceiveListener = function () {
  this.meshReceivedListener = (event) => this.onMeshReceived(event.geom);
  this.meshFailedListener = (event) => this.onMeshError(event.hash);
  this.viewer3DImpl.geomCache().addEventListener(MESH_RECEIVE_EVENT, this.meshReceivedListener);
  this.viewer3DImpl.geomCache().addEventListener(MESH_FAILED_EVENT, this.meshFailedListener);

  this.materialReceiveListener = (event) => this.onMaterialLoaded(event.material, event.hash);
  this.viewer3DImpl.geomCache().addEventListener(MATERIAL_RECEIVE_EVENT, this.materialReceiveListener);
  this.viewer3DImpl.geomCache().addEventListener(MATERIAL_FAILED_EVENT, this.materialReceiveListener);

  this.viewer3DImpl.geomCache().modelAdded(this.options.acmSessionId, this._lineageUrn);
};

OtgLoader.prototype.removeMeshReceiveListener = function () {
  if (!this.meshReceivedListener) {
    return;
  }
  this.viewer3DImpl.geomCache().modelRemoved();
  this.viewer3DImpl.geomCache().removeEventListener(MESH_RECEIVE_EVENT, this.meshReceivedListener);
  this.viewer3DImpl.geomCache().removeEventListener(MESH_FAILED_EVENT, this.meshFailedListener);
  this.viewer3DImpl.geomCache().removeEventListener(MATERIAL_RECEIVE_EVENT, this.materialReceiveListener);
  this.viewer3DImpl.geomCache().removeEventListener(MATERIAL_FAILED_EVENT, this.materialReceiveListener);
  this.meshReceivedListener = null;
  this.meshFailedListener = null;
};

function getBasePath(path) {
  var basePath = "";
  var lastSlash = path.lastIndexOf("/");
  if (lastSlash != -1)
  basePath = path.substr(0, lastSlash + 1);
  return basePath;
}

function getQueryParams(options) {
  return options.acmSessionId ? "acmsession=" + options.acmSessionId : "";
}

function createLoadContext(options, basePath, queryParams) {
  var loadContext = {
    basePath: basePath,
    objectIds: options.ids,
    globalOffset: options.globalOffset,
    fragmentTransformsDouble: options.fragmentTransformsDouble,
    placementTransform: options.placementTransform,
    applyRefPoint: options.applyRefPoint,
    queryParams: queryParams,
    bvhOptions: options.bvhOptions || { isWeakDevice: isMobileDevice() },
    applyScaling: options.applyScaling,
    forceWorldUpDirection: options.forceWorldUpDirection,
    overrideModelUpDirection: options.overrideModelUpDirection,
    overrideDistanceUnit: options.overrideDistanceUnit,
    applyPlacementInModelUnits: options.applyPlacementInModelUnits,
    loadInstanceTree: options.loadInstanceTree,
    processExtraViewpoints: options.processExtraViewpoints
    // avoidNwcRotation: options.avoidNwcRotation,
  };

  return initLoadContext(loadContext);
}

OtgLoader.prototype.loadFile = function (path, options, onDone, onWorkerStart) {
  if (this.loadAborted()) {
    logger.log("SVF loader was already destructed. So no longer usable.");
    return false;
  }

  this.viewer3DImpl._signalNoMeshes();

  if (this.loading) {
    logger.log("Loading of SVF already in progress. Ignoring new request.");
    return false;
  }

  // Mark it as loading now.
  this.loading = true;

  //For OTG server, the URN of the manifest is used as part of the ACM session token
  if (options.acmSessionId) {
    //TODO: initWorker should be updated to also send the acmsession when authorizing the web worker,
    //in a followup change.
    this.svfUrn = options.acmSessionId.split(",")[0];
  } else {
    //If the URN is not supplied, we can derive it from the storage path,
    //but that will only work for URNs that are not shallow copies.
    console.warn("DEPRECATED: Automatic derivation of URN will be removed in a future release. Please set the acmSessionId parameter when loading OTG data.");

    var idx = path.indexOf(FLUENT_URN_PREFIX);
    if (idx === -1) {
      idx = path.indexOf(DS_OTG_CDN_PREFIX);
    }

    if (idx !== -1) {

      //This will work for WIP URNs but probably not OSS ones, where
      //the URN is an encoding of the OSS file name or something equally arbitrary
      var parts = path.split("/");
      var seed = parts[1];
      var version = parts[2];
      var urn = seed + "?version=" + version;
      var encoded = av.toUrlSafeBase64(urn);

      this.svfUrn = encoded;
    } else {
      //This just makes sure the Urn is not null in cases of loading from local path that doesn't have a URN at all
      this.svfUrn = av.toUrlSafeBase64(path);
    }
  }

  this.sharedDbPath = options.sharedPropertyDbPath;

  this.currentLoadPath = path;

  this.basePath = getBasePath(path);
  this.acmSessionId = options.acmSessionId;

  this.options = options;
  this.queryParams = getQueryParams(options);

  this.loadContext = createLoadContext(options, this.basePath, this.queryParams);

  // The request failure parameters received by onFailureCallback (e.g. httpStatusCode) cannot just be forwarded to onDone().
  // Instead, we have to pass them through ViewingService.defaultFailureCallback, which converts them to error parameters
  // and calls loadContext.raiseError with them.
  this.loadContext.raiseError = function (code, msg, args) {
    var error = { "code": code, "msg": msg, "args": args };
    onDone && onDone(error);
  };
  this.loadContext.onFailureCallback = ViewingService.defaultFailureCallback.bind(this.loadContext);

  // For fine grained uniform updates, we need the FragmentList to be able to map dbIds to fragIds (see FragmentList.setThemingColor)
  if (this.viewer3DImpl.isWebGPU() || USE_MULTI_MATERIAL_RENDER_CALLS) {
    this.loadContext.buildDbId2FragIdMap = true;
  }

  this._selectiveLoadingController.prepare(options);

  this.loadModelRoot(this.loadContext, onDone);

  //We don't use a worker for OTG root load, so we call this back immediately
  //We will use the worker for heavy tasks like BVH compute after we get the model root file.
  onWorkerStart && onWorkerStart();

  return true;
};

OtgLoader.prototype._requestRepaint = function () {
  if (!this.viewer3DImpl?.modelVisible(this.model.id)) {
    return;
  }
  this.viewer3DImpl.api.dispatchEvent({ type: av.LOADER_REPAINT_REQUEST_EVENT,
    loader: this, model: this.model });
};

OtgLoader.prototype.loadAborted = function () {
  return this.viewer3DImpl === null;
};

OtgLoader.prototype.loadModelRoot = async function (loadContext, onDone) {
  this.t0 = new Date().getTime();
  this.firstPixelTimestamp = null;
  var scope = this;

  this.svf = new OtgPackage();
  this.svf.loadDone = false;

  this.svf.basePath = loadContext.basePath;

  const geomCache = this.viewer3DImpl.geomCache(); // call this early so the workers can be initialized

  const metadataPromise = this.svf.loadMetadata(this.loadContext, pathToURL(this.currentLoadPath), this.onViewpointDataLoaded.bind(this));
  this.svf.initializeDbIdFilter(this.loadContext.objectIds);

  // material hashlist is always loaded from the server
  this.svf.loadMaterialHashList(loadContext);

  await metadataPromise;
  if (this.loadAborted()) {
    return;
  }

  this.onModelRootLoadDone();
  onDone?.(null, this.model);

  if (BVH_CACHING && !this.options?.disableCache && !this._selectiveLoadingController.isActive) {
    // TODO let tryLoadFromCache run async while waiting for metadataPromise. At the moment though,
    // we need placementWithOffset from the metadata for the cache key. Also, the this.model access needs to wait for metadataPromise.
    await this.tryLoadFromCache();
    if (this.loadAborted()) {
      return;
    }
    if (this.loadFromCache) {
      return;
    }
  }

  this.svf.loadGeometryHashList(loadContext);
  this.svf.loadFragmentList(loadContext, this.onFragmentLoaded.bind(this), this.onAllFragmentsLoaded.bind(this));

  if (USE_OUT_OF_CORE_TILE_MANAGER) {
    // We inform the geom cache, that we are currently downloading the fragment list.
    // While this is in process, we will throttle the download of the geometry data,
    // because the fragment list is more important to be able to compute the BVH
    // as soon as possible and get better geometry prioritization afterwards.
    geomCache.throttleDownload();
  }

  scope.makeBVHInWorker();
};

OtgLoader.prototype.onFragmentLoaded = function (fragmentID) {
  if (!this.options.skipMeshLoad) {
    if (this._selectiveLoadingController.isActive) {
      this._selectiveLoadingController.onFragmentReady(fragmentID);
    } else {
      if (!USE_GEOMETRY_OOC) {
        // With geometry OOC, LoadGeometryTask calls loadFragments, which calls tryToActivateFragment.
        this.tryToActivateFragment(fragmentID, "fragment");
      }
    }
  }

  // Optional: Track fragment load progress separately
  if (this.options.onFragmentListLoadProgress) {
    this.trackFragmentListLoadProgress();
  }
};

OtgLoader.prototype.onAllFragmentsLoaded = function () {
  //For 3D models, we can start loading the property database as soon
  //as we know the fragment list which contains the fragId->dbId map.
  if (!this.options.skipPropertyDb) {
    this.loadPropertyDb();
    this._selectiveLoadingController.onPropertiesReady();
  }

  // If this flag is false, some data is not ready yet. E.g. fragments.fragId2DbId is initially
  // filled with zeros and is only be usable after root looad. Note that fragDataLoaded = true
  // does NOT mean yet that geometry and materials are all loaded.
  this.fragmentDataLoaded = true;

  this._selectiveLoadingController.onFragmentListReady();

  this.viewer3DImpl.api.fireEvent({ type: et.MODEL_ROOT_LOADED_EVENT, svf: this.svf, model: this.model });

  if (this.options.skipMeshLoad || !this.svf.fragments.length) {
    this.onGeomLoadDone();
  } else {
    this.onOperationComplete("fraglist loaded");
  }

  if (USE_OUT_OF_CORE_TILE_MANAGER) {
    this.tryEarlyOutOfCoreBVHCompute();
  }
};

OtgLoader.prototype.tryLoadFromCache = async function () {
  const version = 10;
  const urn = this.options.bubbleNode?.getRootNode().urn(); // bubbleNode does not exist when using viewer.start(url). fall back to basePath.
  this.hlodCacheBucket = "hlod_cache_v" + version + "_" + (urn ? Autodesk.Viewing.fromUrlSafeBase64(urn) : this.basePath);

  this.hlodCacheIdentifier = this.getHLODCacheIdentifier();

  // this has to be done beforehand since crypto.subtle.digest would stall frag list chunk writing and therefore mess up control flow if data is updated while writing
  this.fragListHashIdentifier = new Uint8Array(await digest("SHA-1", `${this.hlodCacheIdentifier}_frag_list`));

  // cancel loading, if cache is incomplete. we can improve this once frag list chunks are available to load from the server
  if (!USE_SERVER_SIDE_BVH && !this.getHLODCachedFlag()) {
    this.loadFromCache = false;
    return;
  }

  const bvh = await this.getCachedBVH();
  if (!bvh) {
    // reset flag in case the cache was emptied
    this.setHLODCachedFlag(false);
    this.loadFromCache = false;
    return;
  }
  if (this.loadAborted()) {
    return;
  }

  this.setBVH(bvh);

  // Precise boxes are contained in the cached fragment list
  this.model.getFragmentList().fragments.dontComputeBoxes = true;

  this.svf.initializeGeometryHashList(/*geometryHashesByteStride*/20, /*geometryHashesVersion*/1);

  this.loadFromCache = true;
};

OtgLoader.prototype.makeBVHInWorker = function () {

  var scope = this;

  //We can kick off the request for the fragments-extra file, needed
  //for the BVH build as soon as we have the metadata (i.e. placement transform)
  //Do this on the worker thread, because the BVH build can take a while.
  var workerContext = Object.assign({}, scope.loadContext);
  workerContext.operation = WORKER_LOAD_OTG_BVH;
  workerContext.raiseError = null;
  workerContext.onFailureCallback = null;
  workerContext.fragments_extra = pathToURL(scope.basePath) + scope.svf.manifest.assets.fragments_extra;
  workerContext.placementTransform = scope.svf.placementTransform;
  workerContext.placementWithOffset = scope.svf.placementWithOffset;
  workerContext.fragmentTransformsOffset = scope.svf.metadata.fragmentTransformsOffset;
  workerContext.globalOffset = scope.svf.globalOffset;

  if (workerContext.fragments_extra) {
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      this.svf.loadAsyncResource(scope.loadContext, workerContext.fragments_extra, "", function (data) {
        if (scope.loadAborted()) {
          return;
        }
        scope.svf.extraFragInfo = new OtgFragInfo(data, workerContext);

        // Operation complete will be called within tryEarlyOutOfCoreBVHCompute, once FragmentList, FragExtras and BVH
        // are ready.
        scope.tryEarlyOutOfCoreBVHCompute();
      });
    } else {
      scope.bvhWorker = createWorker('OtgBvhWorker');

      var onOtgWorkerEvent = function (e) {
        if (scope.loadAborted()) {
          return;
        }

        console.log("Received BVH from worker");

        var bvh = new BVH(e.data.bvh.nodes.nodesRaw, e.data.bvh.primitives, scope.options.bvhOptions,
        e.data.bvh.fragId2nodeId, e.data.bvh.nodeParents);

        // boxes should be set first so that selectiveLoadingController in setBVH can use them
        scope.model.setFragmentBoundingBoxes(e.data.boxes, e.data.boxStride);
        scope.setBVH(bvh, false);

        scope.bvhWorker.terminate();
        scope.bvhWorker = null;
      };

      scope.bvhWorker.addEventListener('message', onOtgWorkerEvent);

      scope.bvhWorker.doOperation(workerContext);
    }
  } else {
    // If the model does not reference a fragment_extra file, the worker would not do anything.
    // This is okay for empty models. For this case, just skip the BVH phase to avoid the load progress from hanging.
    scope.onOperationComplete("bvh");
  }
};

OtgLoader.prototype.setBVH = function (bvh, initializeOoctm) {
  this.model.setBVH(bvh, initializeOoctm);
  this._selectiveLoadingController.onBoundingVolumeHierarchyReady();
  this._requestRepaint();
  this.onOperationComplete("bvh");
};

OtgLoader.prototype.getHLODCacheIdentifier = function () {
  const params = {
    bvhOptions: this.options.bvhOptions,
    placementWithOffset: this.svf.placementWithOffset,
    viewGuid: this.options.bubbleNode?.guid() // bubbleNode is not set when using viewer.start(url). In that case there are no views and we don't need the guid.
  };
  return this.hlodCacheBucket + JSON.stringify(params);
};

OtgLoader.prototype.getCachedBVH = async function () {

  const geomCache = this.viewer3DImpl.geomCache();

  const data = await geomCache.getResource(this.hlodCacheBucket, await digest("SHA-1", `${this.hlodCacheIdentifier}_bvh`));

  if (!data) {
    return null;
  }

  const header = new Uint16Array(data.buffer, 0, 2); // currently unused
  const headerSize = header.byteLength;
  const nodesOffset = headerSize;

  // BVHBuilder.NodeArray class expects this to be a separate buffer, hence the copy
  const nodes = data.slice(nodesOffset).buffer;
  const nodeArray = new NodeArray(nodes, false);

  // Assign new primStart to all nodes.
  // Long term, we want to partially load the fraglist and probably assign primStart/fragIds in whichever order they come in.
  // Until then, having deterministic fragIds is very nice for debugging.
  let primCount = 0;
  for (let i = 0; i < nodeArray.nodeCount; i++) {
    nodeArray.setPrimStart(i, primCount);
    primCount += nodeArray.getPrimCount(i);
  }

  // don't use primitives, fragments of a batch will be in the fraglist consecutively without indirection
  const primitives = null;

  return new BVH(nodes, primitives, this.options.bvhOptions);
};

OtgLoader.prototype.writeToCache = async function () {
  const success = (await this.cacheBVH()) && (await this.cacheFragList());
  if (success) {
    this.setHLODCachedFlag(true);
  }
};

OtgLoader.prototype.cacheBVH = async function () {

  const geomCache = this.viewer3DImpl.geomCache();
  let bvhIterator = this.model.getIterator();
  const bvh = bvhIterator.getBVH();

  const headerSize = 4; // header is currently unused
  const nodesOffset = headerSize;
  const nodesByteLength = bvh.nodes.nodesRaw.byteLength;

  const buffer = new Uint8Array(headerSize + nodesByteLength);

  // primStart is computed when reading from cache. It's not needed for caching but we don't want to change the node array layout,
  // so we fill it with dummy values to make things break faster if anyone would read it.
  const nodesCopy = new NodeArray(bvh.nodes.nodesRaw.slice(), false);
  for (let i = 0; i < nodesCopy.nodeCount; i++) {
    nodesCopy.setPrimStart(i, 0xFFFFFFFF);
  }
  buffer.set(new Uint8Array(nodesCopy.nodesRaw), nodesOffset);

  return geomCache.opfsStore(this.hlodCacheBucket, [`${this.hlodCacheIdentifier}_bvh`], [buffer]);
};

OtgLoader.prototype.cacheFragList = async function () {
  const geomCache = this.viewer3DImpl.geomCache();

  const bvh = this.model.getIterator().getBVH();
  const nodes = bvh.nodes;
  const primitives = bvh.primitives;
  const frags = this.model.getFragmentList().fragments;

  const geometryHashesByteStride = this.svf.geomMetadata.byteStride;

  const fragListHash = new Uint8Array(this.fragListHashIdentifier);
  const fragListHash16 = new Uint16Array(fragListHash.buffer);
  const fragListHash32 = new Uint32Array(fragListHash.buffer);

  const identifiers = [];
  const datas = [];

  bvh.traverseBreadthFirst((nodeIdx) => {
    const primStart = nodes.getPrimStart(nodeIdx);
    const primCount = nodes.getPrimCount(nodeIdx);

    if (primCount === 0) {
      return;
    }

    // We can't write the hashes into the output buffer directly since we don't know the amount. Collect them first.
    const geometryHashes = [];
    const chunkGeomIds = []; // indices into geometryHashes. 1-based, 0 is reserved for INVALID_GEOM_ID
    const modelGeomIdToChunkGeomId = new Map();
    modelGeomIdToChunkGeomId.set(INVALID_GEOM_ID, INVALID_GEOM_ID);

    for (let i = 0; i < primCount; i++) {
      const fragId = primitives[primStart + i];

      const modelGeomId = frags.geomDataIndexes[fragId];
      let chunkGeomId = modelGeomIdToChunkGeomId.get(modelGeomId);
      if (chunkGeomId === undefined) {
        chunkGeomId = geometryHashes.length + 1; // + 1 because of INVALID_GEOM_ID

        let hash = this.svf.getGeometryHash(modelGeomId);
        geometryHashes.push(hash);
        modelGeomIdToChunkGeomId.set(modelGeomId, chunkGeomId);
      }
      chunkGeomIds[i] = chunkGeomId;
    }

    const headerSizeBytes = 4;
    const chunkGeomIdBufBytes = (primCount + primCount % 2) * 2; // 2 bytes per chunk geom ID, 2 bytes padding if odd number of IDs
    const buffer = new Uint8Array(headerSizeBytes + chunkGeomIdBufBytes + 21 * 4 * primCount + geometryHashes.length * geometryHashesByteStride);

    // you see, even though this is about writing, we are "reading" typed arrays from `buffer`, so it makes complete sense to use InputStream here.
    const stream = new InputStream(buffer);
    const header = stream.getTypedArray(Uint16Array, headerSizeBytes / 2);
    const chunkGeomIdBuf = stream.getTypedArray(Uint16Array, primCount);
    stream.padTo4Bytes();
    const materialIds = stream.getTypedArray(Uint32Array, primCount);
    const dbIds = stream.getTypedArray(Uint32Array, primCount);
    const flagsAndPolygonCounts = stream.getTypedArray(Uint32Array, primCount);
    const boxes = stream.getTypedArray(Float32Array, 6 * primCount);
    const transforms = stream.getTypedArray(Float32Array, 12 * primCount);
    const geometryHashesBuffer = stream.getTypedArray(Uint8Array, geometryHashes.length * geometryHashesByteStride);
    console.assert(stream.offset === buffer.byteLength);

    header[0] = 0; // version, unused
    header[1] = geometryHashes.length;

    // collect other data, write to buffer
    for (let i = 0; i < primCount; i++) {
      const fragId = primitives[primStart + i];
      dbIds[i] = frags.fragId2dbId[fragId];
      materialIds[i] = frags.materials[fragId];
      chunkGeomIdBuf[i] = chunkGeomIds[i];

      let flagsAndPolygonCount = 0;
      if (frags.visibilityFlags[fragId] & MeshFlags.MESH_NOT_FOR_DISPLAY) {// this is the only MeshFlag that comes from the downloaded fraglist. Everything else is runtime-generated
        flagsAndPolygonCount |= 0x01000000;
      }
      flagsAndPolygonCount |= this.svf.extraFragInfo.getPolygonCount(fragId) & 0x00FFFFFF;
      flagsAndPolygonCounts[i] = flagsAndPolygonCount;

      for (let j = 0; j < 6; j++) {
        boxes[i * 6 + j] = frags.boxes[fragId * 6 + j];
      }

      for (let j = 0; j < 12; j++) {
        transforms[i * 12 + j] = frags.transforms[fragId * 12 + j];
      }
    }

    for (let i = 0; i < geometryHashes.length; i++) {
      packedToBin(geometryHashes[i], geometryHashesBuffer, i * geometryHashesByteStride);
    }

    fragListHash32[4] = nodeIdx; // encode nodeId in last 4 bytes
    identifiers.push(bin16ToPackedString(fragListHash16));
    datas.push(buffer);
  });

  return geomCache.opfsStore(this.hlodCacheBucket, identifiers, datas);
};

OtgLoader.prototype.getCachedFragListChunk = async function (chunkId, priority) {
  const geomCache = this.model.loader.viewer3DImpl.geomCache();

  const fragListHash = new Uint8Array(this.fragListHashIdentifier);
  const fragListHash16 = new Uint16Array(fragListHash.buffer);
  const fragListHash32 = new Uint32Array(fragListHash.buffer);

  fragListHash32[4] = chunkId; // encode chunkId in last 4 bytes

  return await geomCache.getResource(this.hlodCacheBucket, fragListHash16.buffer, priority);
};

// BUG: BIMCAS-30671:
// - We had a case where chunkIds were processed multiple times, which led to a model never finish loading
//     - See below: `this.svf.fragments.numLoaded === this.svf.metadata.stats.num_fragments` is never true.
// - This was caused by OutOfCoreTileManager::freeAllNodes(..) being called while processing fragList chunks was still in progress.
//     - In the EUI caused by `viewer.setView(..)` calling `OutOfCoreTileManager::freeAllNodes(..)`
// FIX:
// - Keep track of processed chunkIds and avoid adding a new LoadFraglistTask if the chunk has already been processed.
OtgLoader.prototype.hasChunkBeenProcessed = function (chunkId) {
  return this.processedChunkIds?.has(chunkId) ?? false;
};

// this should mirror Otg.js:readOneFragment
OtgLoader.prototype.processFragListChunk = function (chunkId, data) {
  // We should never process the same chunk twice.
  console.assert(!this.processedChunkIds.has(chunkId), `Chunk ${chunkId} has already been processed.`);

  this.processedChunkIds.add(chunkId);

  const model = this.model;
  const svf = model.loader.svf;

  const frags = model.getFragmentList().fragments;
  const geometryHashesByteStride = svf.geomMetadata.byteStride;

  const bvh = model.getIterator().getBVH();
  const nodes = bvh.nodes;
  const primitives = bvh.primitives;
  const primStart = nodes.getPrimStart(chunkId);
  const primCount = nodes.getPrimCount(chunkId);

  const headerSizeBytes = 4;

  const stream = new InputStream(data);
  const header = stream.getTypedArray(Uint16Array, headerSizeBytes / 2);

  // const version = header[0]; // unused
  const geometryHashesLength = header[1];

  const chunkGeomIdToModelGeomId = new Map();
  chunkGeomIdToModelGeomId.set(INVALID_GEOM_ID, INVALID_GEOM_ID);

  const chunkGeomIdBuf = stream.getTypedArray(Uint16Array, primCount);
  stream.padTo4Bytes();
  const materialIds = stream.getTypedArray(Uint32Array, primCount);
  const dbIds = stream.getTypedArray(Uint32Array, primCount);
  const flagsAndPolygonCounts = stream.getTypedArray(Uint32Array, primCount);
  const boxes = stream.getTypedArray(Float32Array, 6 * primCount);
  const transforms = stream.getTypedArray(Float32Array, 12 * primCount);
  const geometryHashesBuffer = stream.getTypedArray(Uint8Array, geometryHashesLength * geometryHashesByteStride);
  console.assert(stream.offset === data.buffer.byteLength);

  for (let i = 0; i < geometryHashesLength; i++) {
    const hash = binToPackedString(geometryHashesBuffer, i * geometryHashesByteStride, geometryHashesByteStride);
    let modelGeomId = this.hashToGeomId.get(hash);
    if (modelGeomId === undefined) {
      modelGeomId = this.hashToGeomId.size + 1; // + 1 because of INVALID_GEOM_ID
      this.hashToGeomId.set(hash, modelGeomId);
      this.svf.geomMetadata.hashes[modelGeomId] = hash;
    }
    chunkGeomIdToModelGeomId.set(i + 1, modelGeomId); // + 1 because of INVALID_GEOM_ID
  }


  for (let i = 0; i < primCount; i++) {
    const fragId = primitives?.[primStart + i] ?? primStart + i;
    frags.fragId2dbId[fragId] = dbIds[i];
    frags.materials[fragId] = materialIds[i];
    const chunkGeomId = chunkGeomIdBuf[i];
    frags.geomDataIndexes[fragId] = chunkGeomIdToModelGeomId.get(chunkGeomId);

    const flagsAndPolygonCount = flagsAndPolygonCounts[i];
    if (flagsAndPolygonCount & 0x01000000) {
      frags.visibilityFlags[fragId] |= MeshFlags.MESH_NOT_FOR_DISPLAY;
      // LMV-6230: Unset MESH_VISIBLE flag in favor of setting MESH_HIDE as Model Browser controls visibility using MESH_VISIBLE
      frags.visibilityFlags[fragId] &= ~MeshFlags.MESH_VISIBLE;
    }

    for (let j = 0; j < 6; j++) {
      frags.boxes[fragId * 6 + j] = boxes[i * 6 + j];
    }

    for (let j = 0; j < 12; j++) {
      frags.transforms[fragId * 12 + j] = transforms[i * 12 + j];
    }

    if (this.loadContext.buildDbId2FragIdMap) {
      svf.addDbIdReference(fragId, frags.fragId2dbId[fragId]);
    }

    this.onFragmentLoaded(fragId);
    this.svf.fragments.numLoaded++;
  }

  // This should never happen, if it does it mean we are probably double counting / processing some chunks multiple times
  console.assert(this.svf.fragments.numLoaded <= this.svf.metadata.stats.num_fragments, `Too many fragments loaded: ${this.svf.fragments.numLoaded} > ${this.svf.metadata.stats.num_fragments}`);

  if (this.svf.fragments.numLoaded === this.svf.metadata.stats.num_fragments) {
    this.hashToGeomId = null; // not needed anymore
    this.onAllFragmentsLoaded();
    model.boxesLoadingDone(); // In this case, boxes come from the fragment list. They're done when the fraglist is done.
  }
};

OtgLoader.prototype.setHLODCachedFlag = function (value) {
  LocalStorage.setItem(this.hlodCacheIdentifier, value);
};

OtgLoader.prototype.getHLODCachedFlag = function () {
  return JSON.parse(LocalStorage.getItem(this.hlodCacheIdentifier));
};

OtgLoader.prototype.stopLoadingFragment = function (fragId) {
  this.model.getFragmentList().setFlagFragment(fragId, MeshFlags.MESH_NOTLOADED, true);
  this.model.getFragmentList().setFragOff(fragId, true);
  this.trackFragLoadProgress();
};

//Attempts to turn on display of a received fragment.
//If the geometry or material is missing, issue requests for those
//and delay the activation. Once the material or mesh comes in, they
//will attempt this function again.
OtgLoader.prototype.tryToActivateFragment = function (fragId, whichCaller) {
  if (this.loadAborted()) {
    return;
  }

  var svf = this.svf;

  var geomId = svf.fragments.geomDataIndexes[fragId];

  const isNotForDisplay = svf.fragments.visibilityFlags[fragId] & MeshFlags.MESH_NOT_FOR_DISPLAY;

  const skipLoad = svf.dbIdFilter && !svf.dbIdFilter[svf.fragments.fragId2dbId[fragId]] ||
  this._selectiveLoadingController.isActive && !this._selectiveLoadingController.isFragmentPassing(fragId) ||
  this.options.skipHiddenFragments && isNotForDisplay;

  if (isNotForDisplay || skipLoad) {
    // inform the renderer that a hidden fragment has occurred (will be rendered as ghosted if not skipped)
    this.model.getFragmentList().allVisible = false;
  }

  if (skipLoad) {
    this.stopLoadingFragment(fragId);
    return;
  }

  var haveToWait = false;

  //The tryToActivate function can be called up to three times, until all the
  //needed parts are received.
  // Before we can safely consider a fragment as finished, we must make sure that there are no pending
  // tryToActivate(fragId, "material") or "geometry" calls that will come later.

  //1. Check if the material is done

  var material = null;
  var materialId = svf.fragments.materials[fragId];
  var matHash = svf.getMaterialHash(materialId);

  if (matHash !== null) {
    material = this.findOrLoadMaterial(this.model, matHash);
    if (material === null) {

      if (whichCaller === "fragment") {
        //Material is not yet available, so we will delay turning on the fragment until it arrives
        this.pendingMaterials.get(matHash).push(fragId);
        this.pendingRequests++;
      }

      if (whichCaller !== "material") {
        if (!this.failedMaterials.has(matHash)) {
          // We have to wait if the material has not been loaded yet, but if it failed,
          // we continue and use the default material below.
          haveToWait = true;
        }
      } else {
        //it means the material failed to load, so we won't wait for it.
        this.failedMaterials.add(matHash);
      }
    } else {
      // Materials are shared across models, so for already loaded materials with the same hash we need to ref count the current model
      if (whichCaller === "fragment") {
        this.viewer3DImpl.matman()._addMaterialRef(material, this.model.id);
      }
    }
  } else {
    // We don't have to wait if material hash is null, as this indicates an invalid material index (see INVALID_MAT_ID).
    console.warn("Material hash is null for fragment ", fragId);
  }

  //2. Check if the mesh is done

  // Invalid geometry shouldn't block overall progress -- mark the fragment as success.
  if (geomId === INVALID_GEOM_ID) {
    if (material || whichCaller === "material") {
      // A fragment with null-geom may still have a material. If so, we wait for the material before we consider it as finished.
      // This makes sure that we don't count this fragment twice. Note that we cannot just check whichCaller==="fragment" here:
      // This would still cause errors if the material comes in later after onGeomLoadDone().
      this.stopLoadingFragment(fragId);
    }
    return;
  }

  var geom = this.model.getGeometryList().getGeometry(geomId);
  if (!geom) {

    if (whichCaller === "fragment") {
      //Mesh is not yet available, so we will request it and
      //delay turning on the fragment until it arrives
      this.loadGeometry(geomId, fragId);
    }

    haveToWait = true;
  } else {
    if (whichCaller === "fragment") {
      this.model.getGeometryList().addInstance(geomId);
    }
  }

  if (haveToWait)
  return;

  //if (this.options.createWireframe)
  //    createWireframe(geom, this.tmpMatrix);

  //We get the matrix from the fragments and we pass it back into setupMesh
  //with the activateFragment call, but this is to maintain the
  //ability to add a plain THREE.Mesh -- otherwise it could be simpler
  this.model.getFragmentList().getOriginalWorldMatrix(fragId, this.tmpMatrix);

  var m = this.viewer3DImpl.setupMesh(this.model, geom, matHash, this.tmpMatrix);

  //If there is a placement transform, we tell activateFragment to also recompute the
  //world space bounding box of the fragment from the raw geometry model box, for a tighter
  //fit compared to what we get when loading the fragment list initially.
  this.model.activateFragment(fragId, m, !!svf.placementTransform);

  // pass new fragment to Geometry cache to update priority
  // TODO: Check if we can determine the bbox earlier, so that we can also use it to prioritize load requests
  //       from different OtgLoaders.
  this.viewer3DImpl.geomCache().updateGeomImportance(this.model, fragId);

  this.trackFragLoadProgress();

};

OtgLoader.prototype.onViewpointDataLoaded = function () {
  if (this.loadAborted()) {
    return;
  }
  // Load extra materials if viewpoints enabled as some materials are needed for override Sets
  if (this.svf.needExtraMaterials) {
    // Was extra materials loading kicked off already?
    if (!this.svf.extraMaterialLoaded) {
      this.svf.extraMaterialLoaded = true;
      for (const matHash of Object.values(this.svf.extraMatHashes)) {
        const mat = this.findOrLoadMaterial(this.model, matHash);
        if (!mat) {
          this.missingExtraMaterials.add(matHash);
        }
      }
    }
  }

  if (this.missingExtraMaterials.size === 0) {
    this.onOperationComplete("viewpoints and extra materials");
  }

  // Handle viewpoint tree based on processExtraViewpoints flag
  if (this.svf.viewpointTreeRoot) {
    if (this.svf.processExtraViewpoints) {
      // Merge mode: merge VTR into manifest tree, append unmatched nodes as "Saved Viewpoints"
      if (this.options.addViews) {
        this.options.addViews(this.svf.viewpointTreeRoot, this.model);
      }
    } else {
      // Hydrate mode: Keep existing manifest views and hydrates views with viewpoint data
      if (this.options.hydrateViews) {
        this.options.hydrateViews(this.svf, this.model);
      }
    }
  }
};

OtgLoader.prototype.onModelRootLoadDone = function () {

  const svf = this.svf;

  // counts fully loaded fragments (including geometry and material)
  svf.fragsLoaded = 0;

  // number of loaded fragments (also the ones for which we didn't load material and geom yet)
  svf.fragsLoadedNoGeom = 0;

  svf.nextRepaintPolys = 0;
  svf.numRepaints = 0;

  svf.urn = this.svfUrn;
  svf.acmSessionId = this.acmSessionId;

  svf.loadOptions = this.options || {};

  this.t1 = Date.now();

  this.model = new Model(svf, this);

  this._selectiveLoadingController.onModelRootReady(this.model);

  var metadataStats = {
    category: "metadata_load_stats",
    urn: svf.urn,
    has_topology: !!svf.topology,
    has_animations: !!svf.animations,
    materials: svf.metadata.stats.num_materials,
    is_mobile: isMobileDevice()
  };
  logger.track(metadataStats);

  this.viewer3DImpl.signalProgress(ROOT_LOADED_PROGRESS, ProgressState.ROOT_LOADED, this.model, svf.metadata.stats.num_fragments);

  svf.handleHashListRequestFailures(this.loadContext);

  svf.propDbLoader = PropDbLoader.getProvider().create(this.sharedDbPath, this.model, this.viewer3DImpl.api);

  // Ideally this would be just this._lineageUrn = model.getDocumentNode().lineageUrn(), but:
  // getDocumentNode() can be null (there was a customer report, we don't know how that happened, see also https://git.autodesk.com/A360/firefly.js/pull/6349),
  // in which we use the identical svf.urn. However, we can not simply always use svf.urn, because it is not set when loading models locally (e.g. DiffTool tests)
  // Also models uploaded directly to OSS (not via WipDM) don't have a lineage, so lineageUrn returns null.
  this._lineageUrn = this.model.getDocumentNode()?.lineageUrn();
  if (!this._lineageUrn) {
    this._lineageUrn = BubbleNode.parseLineageUrnFromDecodedUrn(Autodesk.Viewing.fromUrlSafeBase64(svf.urn));
  }

  this.addMeshReceiveListener();

  // We don't call invalidate here: At this point, the model is not added to the viewer yet (see onSuccess()
  // in Viewer3D.loadModel). So, invalidating would just let other models flicker.
};


// Returns geometry loading progress in integer percent
function getProgress(svf) {
  return Math.floor(100 * svf.fragsLoaded / svf.metadata.stats.num_fragments);
}

// Called whenever a geom load request is finished or or has failed.
OtgLoader.prototype.trackFragLoadProgress = function () {
  var svf = this.svf;

  // Inc frag counter and track progress in percent
  var lastPercent = getProgress(svf);

  svf.fragsLoaded++;

  var curPercent = getProgress(svf);

  // Signal progress, but not for each single fragment. Just if the percent value actually changed.
  // (we won't report 100% here, because onLoadComplete in viewer3DImpl will do that)
  if (curPercent > lastPercent && curPercent !== 100) {
    // We report 5% when the root has been loaded, so we rescale the progress to start at 5%
    this.viewer3DImpl.signalProgress(ROOT_LOADED_PROGRESS + 95 / 100 * curPercent, ProgressState.LOADING, this.model, svf.metadata.stats.num_fragments);
  }

  //repaint every once in a while -- more initially, less as the load drags on.
  var geomList = this.model.getGeometryList();
  if (geomList.geomPolyCount > svf.nextRepaintPolys) {
    if (!this.firstPixelTimestamp) {
      // this is imprecise: between now and the first pixel can be an arbitrary amount of events and also rendering itself.
      this.firstPixelTimestamp = Date.now();
    }
    svf.numRepaints++;
    svf.nextRepaintPolys += 100000 * Math.pow(1.5, svf.numRepaints);
    this._requestRepaint();
  }

  // If this was the last fragment...
  if (svf.fragsLoaded === svf.metadata.stats.num_fragments) {
    // Signal that we are done with mesh loading
    this.onOperationComplete("all fragments activated");
  }

  this.trackOnDemandLoadProgress();
};

OtgLoader.prototype.trackFragmentListLoadProgress = function () {

  var svf = this.svf;
  function getFragListLoadProgress(svf) {
    return Math.floor(100 * svf.fragsLoadedNoGeom / svf.metadata.stats.num_fragments);
  }
  var lastPercent = getFragListLoadProgress(svf);
  svf.fragsLoadedNoGeom++;
  var percent = getFragListLoadProgress(svf);

  if (percent > lastPercent) {
    this.options.onFragmentListLoadProgress(this.model, percent);
  }
};

OtgLoader.prototype.onOperationComplete = function (_operation) {
  this.operationsDone++;

  //Destroy the loader if everything we are waiting to load is done
  if (this.operationsDone === 4)
  this.onGeomLoadDone();
};


OtgLoader.prototype.onMaterialLoaded = function (matObj, matHash, matId) {
  if (this.loadAborted()) {
    return;
  }

  // get fragIds that need this material
  var fragments = this.pendingMaterials.get(matHash);

  // Note that onMaterialLoaded is triggered by an EvenListener on geomCache. So, we may also receive calls
  // for materials that we are not wait for, just because other loaders have requested them in parallel.
  // In this case, we must ignore the event.
  if (!fragments) {
    return;
  }

  var matman = this.viewer3DImpl.matman();

  if (matObj) {

    matObj.hash = matHash;
    try {
      var surfaceMat = matman.convertSharedMaterial(this.model, matObj, matHash);

      TextureLoader.loadMaterialTextures(this.model, surfaceMat, this.viewer3DImpl);

      if (matman.hasTwoSidedMaterials()) {
        this.viewer3DImpl.renderer().toggleTwoSided(true);
      }
    } catch (e) {

      // Material load failed. Nothing to do.
    }
  } else {

    // Material load failed. Nothing to do.
  }
  this.pendingMaterials.delete(matHash);

  for (var i = 0; i < fragments.length; i++) {
    this.pendingRequests--;
    this.tryToActivateFragment(fragments[i], "material");
  }

  this.viewer3DImpl._signalNewMaterialAdded(matObj, this.model);

  // All materials including the override materials due to viewpoints have also been loaded
  if (this.missingExtraMaterials.has(matHash)) {
    this.missingExtraMaterials.delete(matHash);
    if (this.missingExtraMaterials.size === 0) {
      this.onOperationComplete("viewpoints and extra materials");
    }
  }
};

OtgLoader.prototype.findOrLoadMaterial = function (model, matHash) {
  // Early out if matHash is not valid, as `makeSharedResourcePath()` will throw an error.
  if (!matHash) {
    console.error("findOrLoadMaterial called with invalid material hash (null).");
    return null;
  }

  //check if it's already requested, but the request is not complete
  //
  // NOTE: If another OTG loader adds this material during loading, matman.findMaterial(..) may actually succeed already - even if we have a pending request.
  //       However, we consider the material as missing until the request is finished. In this way, only 2 cases are possible:
  //        a) A material was already loaded on first need => No material requests
  //        b) We requested the material => Once the request is finished, tryToActivate() will be triggered
  //           for ALL fragments using the material - no matter whether the material was added meanwhile by someone else or not.
  //
  //       If we would allow to get materials earlier, it would get very confusing to find out when a fragment is actually finished:
  //       Some fragments would be notified when the load request is done, but some would not - depending on timing.
  if (this.pendingMaterials.has(matHash)) {
    return null;
  }

  //Check if it's already in the material manager
  var matman = this.viewer3DImpl.matman();
  var mat = matman.findMaterial(model, matHash);

  if (mat)
  return mat;

  //If it's not even requested yet, kick off the request
  this.pendingMaterials.set(matHash, []);

  var url = this.svf.makeSharedResourcePath(this.loadContext.otg_cdn, "materials", matHash);

  // load geometry or get it from cache
  var geomCache = this.viewer3DImpl.geomCache();
  geomCache.requestMaterial(url, undefined, matHash, undefined, this.queryParams, this._lineageUrn);

  return null;
};

OtgLoader.prototype.loadGeometry = function (geomIdx, fragId) {

  ++this.pendingRequests;

  //check if it's already requested, but the request is not complete
  var frags = this.pendingMeshes[geomIdx];
  if (frags) {
    frags.push(fragId);
    return;
  }

  //If it's not even requested yet, kick off the request
  this.pendingMeshes[geomIdx] = [fragId];

  const geomHash = this.svf.getGeometryHash(geomIdx);

  this.svf.geomMetadata.hashToIndex.set(geomHash, geomIdx);

  const addToQueue = !this.loadFromCache;

  const url = this.svf.makeSharedResourcePath(this.loadContext.otg_cdn, "geometry", geomHash);

  // load geometry or get it from cache
  const geomCache = this.viewer3DImpl.geomCache();
  geomCache.requestGeometry(url, undefined, geomHash, undefined, this.queryParams, this._lineageUrn, addToQueue);
};

OtgLoader.prototype.onMeshError = function (geomHash) {

  var geomIdx = this.svf.geomMetadata.hashToIndex.get(geomHash);

  var frags = this.pendingMeshes[geomIdx];

  if (!frags) {
    // The failed mesh has been requested by other loaders, but not by this one.
    return;
  }

  for (var i = 0; i < frags.length; i++) {
    this.stopLoadingFragment(frags[i]);
    this.pendingRequests--;
  }

  this.svf.geomMetadata.hashToIndex.delete(geomHash);
  this.pendingMeshes[geomIdx] = null;
};

OtgLoader.prototype.onMeshReceived = function (geom) {
  if (this.loadAborted()) {
    console.warn("Received geometry after loader was done. Possibly leaked event listener?", geom.hash);
    return;
  }

  var gl = this.model.getGeometryList();

  var geomId = this.svf.geomMetadata.hashToIndex.get(geom.hash);

  //It's possible this fragment list does not use this geometry
  if (geomId === undefined)
  return;

  var geomAlreadyAdded = gl.hasGeometry(geomId);

  var frags = this.pendingMeshes[geomId];

  //TODO: The instance count implied by frags.length is not necessarily correct
  //because the fragment list is loaded progressively and the mesh could be received
  //before all the fragments that reference it. Here we don't need absolute correctness.
  if (!geomAlreadyAdded)
  gl.addGeometry(geom, frags && frags.length || 1, geomId, this.viewer3DImpl.geomCache());else

  return; //geometry was already received, possibly due to sharing with the request done by another model loader in parallel

  if (this.svf.loadDone && !this.options.onDemandLoading && !this._selectiveLoadingController.isActive) {
    console.error("Geometry received after load was done");
  }

  this.svf.geomMetadata.hashToIndex.delete(geom.hash);
  this.pendingMeshes[geomId] = null;

  if (frags) {
    for (var i = 0; i < frags.length; i++) {
      this.pendingRequests--;
      this.tryToActivateFragment(frags[i], "geom");
    }
  }

  this.viewer3DImpl.signalNewGeometryAdded(geomId, this.model);
};


OtgLoader.prototype.onGeomLoadDone = async function () {

  if (USE_GEOMETRY_OOC) {
    // With Geometry OOC, it could happen that another model gets added, removing geometry from this model again.
    // Then we would need to restart the loading process, but for now it's simpler to just never stop it.
    return;
  }

  // Unless selective loading is active, stop listening to geometry receive events.
  // Since all our geometry is loaded, any subsequent geom receive
  // events are just related to requests from other loaders.
  if (!this.options.onDemandLoading && !this._selectiveLoadingController.isActive) {
    this.removeMeshReceiveListener();
  }
  this.svf.loadDone = true;

  //Note that most materials are probably done already as their geometry
  //is received, so this logic will most likely just trigger the textureLoadComplete event.
  TextureLoader.loadModelTextures(this.model, this.viewer3DImpl);

  //If we were asked to just load the model root / metadata, bail early.
  if (this.options.skipMeshLoad) {
    this.currentLoadPath = null;
    this.viewer3DImpl.onLoadComplete(this.model);
    return;
  }

  // We need to keep a copy of the original fragments
  // transforms in order to restore them after explosions, etc.
  // the rotation/scale 3x3 part.
  // TODO: consider only keeping the position vector and throwing out
  //
  //delete this.svf.fragments.transforms;

  var t2 = Date.now();
  this.loadTime = t2 - this.t0;

  var firstPixelTime = this.firstPixelTimestamp - this.t0;

  // Run optional consolidation step
  if (!USE_OUT_OF_CORE_TILE_MANAGER && this.options.useConsolidation) {
    await this.viewer3DImpl.consolidateModel(this.model, this.options.consolidationMemoryLimit);

    if (this.loadAborted()) {
      return;
    }
  }

  var modelStats = {
    category: "model_load_stats",
    is_f2d: false,
    has_prism: this.viewer3DImpl.matman().hasPrism,
    load_time: this.loadTime,
    geometry_size: this.model.getGeometryList().geomMemory,
    meshes_count: this.svf.metadata.stats.num_geoms,
    fragments_count: this.svf.metadata.stats.num_fragments,
    urn: this.svfUrn
  };
  if (firstPixelTime > 0) {
    modelStats['first_pixel_time'] = firstPixelTime; // time [ms] from SVF load to first geometry rendered
  }
  logger.track(modelStats, true);

  const geomList = this.model.getGeometryList();
  // we are interested in the number of models that use US survey units and Civil3D writes them out
  // into the custom values (the spelling error comes from the implementation in Civil3D)
  const civilDrawingUnit = this.svf.metadata['custom values']['drawingUnits'];
  const imperialToMetricConversion = this.svf.metadata['custom values']['imperialToMetricConvertion'];
  const civilUnit = civilDrawingUnit && imperialToMetricConversion ? civilDrawingUnit === 'Meters' ? civilDrawingUnit : imperialToMetricConversion : undefined;
  // for all other models, we use the distance unit from the metadata
  const distanceUnit = civilUnit || this.svf.metadata['distance unit']['value'];
  const modelWorldUpDirection = this.svf.metadata['world up vector']?.XYZ;
  const cameraWorldUpDirection = [this.viewer3DImpl?.camera?.worldup?.x, this.viewer3DImpl?.camera?.worldup?.y, this.viewer3DImpl?.camera?.worldup?.z];
  const dataToTrack = {
    load_time: this.loadTime,
    polygons: geomList.geomPolyCount,
    fragments: this.model.getFragmentList().getCount(),
    mem_usage: geomList.gpuMeshMemory,
    time_to_first_pixel: firstPixelTime,
    viewable_type: '3d',
    url: this.currentLoadPath,
    urn: this.svfUrn,
    loaded_from_cache: this.loadFromCache,
    ...(distanceUnit ? { drawing_units: distanceUnit } : {}),
    modelWorldUpDirection,
    cameraWorldUpDirection
  };
  avp.analytics.track('viewer.model.loaded', dataToTrack);

  this.currentLoadPath = null;

  this.viewer3DImpl.onLoadComplete(this.model);

  if (this.shouldWriteToCache()) {
    // Cache BVH and fragment list once the OutOfCore BVH is computed
    await computeOutOfCoreBVH(this.model);
    await this.writeToCache();
    if (this.loadAborted()) {
      return;
    }
  }

  // do this after caching the fraglist above, since it needs the hashes.
  this.svf.extraFragInfo = null;
  this.svf.fragments.entityIndexes = null;
  this.pendingMeshes = new Array();
  if (!this.options.onDemandLoading && !this._selectiveLoadingController.isActive) {
    this.svf.geomMetadata.hashes = null;
  }
};

OtgLoader.prototype.shouldWriteToCache = function () {
  // Loading and saving the FragList/BVH from cache is currently not supported when a selective loading controlled is
  // enabled since in those cases only a partial dataset would be stored in the cache.
  return !this.loadFromCache && BVH_CACHING && !this.options?.disableCache && !this._selectiveLoadingController.isActive;
};

OtgLoader.prototype.loadPropertyDb = function () {
  this.svf.propDbLoader.load();
};


OtgLoader.prototype.is3d = function () {
  return true;
};

// Returns promise that resolves to null on failure.
OtgLoader.loadOnlyOtgRoot = function (path, options) {

  // create loadContext
  const basePath = getBasePath(path);
  const queryParams = getQueryParams(options);
  const loadContext = createLoadContext(options, basePath, queryParams);

  // init otg package
  const otg = new OtgPackage();
  otg.basePath = basePath;

  // Just load root json and process its metadata
  const url = pathToURL(path);
  return new Promise((resolve, reject) => {

    loadContext.onFailureCallback = () => resolve(null);
    otg.loadAsyncResource(loadContext, url, "json", function (data) {
      otg.metadata = data;
      otg.manifest = data.manifest;

      // Set numGeoms. Note that this must happen before creating the GeometryList.
      otg.numGeoms = otg.metadata.stats.num_geoms;

      otg.processMetadata(loadContext);

      resolve(otg);
    });
  });
};

/**
 * This function will trigger the computation of the OutOfCore BVH, once all necessary information is available.
 *
 * This function must only by called, when the OutOfCoreTileManager is enabled.
 */
OtgLoader.prototype.tryEarlyOutOfCoreBVHCompute = async function () {
  if (!this.loadFromCache) {
    if (this.fragmentDataLoaded && this.svf.extraFragInfo) {
      // Both fragment list and fragment extras have been loaded, we no longer
      // need to throttle downloading the geometries
      this.viewer3DImpl.geomCache().stopThrottleDownload();

      // Update the bounding boxes (we wait with the update until all fragments and the fragments extra
      // have been loaded
      this.model.setFragmentBoundingBoxes(this.svf.extraFragInfo.boxes,
      this.svf.extraFragInfo.boxStride);

      const bvh = await computeOutOfCoreBVH(this.model);

      // model unloaded in the meantime
      if (this.loadAborted()) return;

      this.setBVH(bvh);

      // With geometry OOC, the other writeToCache call site is never reached as model loading never finishes.
      // So we write to cache as soon as all the data is available.
      if (this.shouldWriteToCache() && USE_GEOMETRY_OOC) {
        await this.writeToCache();
      }
    }
  }
};

// Right after model loading, some fragment data are not available yet.
// E.g. fragments.fragId2DbId will initially contain only zeros and graudally filled afterwards.
// Note that waiting for fragment data doesn't guarantee yet that geometry and materials are already loaded too.
//  @returns {boolean} True when finished, false when canceled or failed.
OtgLoader.prototype.waitForFragmentData = async function () {
  if (this.fragmentDataLoaded) {
    return true;
  }

  const viewer = this.viewer3DImpl.api;
  return new Promise((resolve) => {

    const clearListeners = () => {
      viewer.removeEventListener(av.MODEL_ROOT_LOADED_EVENT, onLoad);
      viewer.removeEventListener(av.MODEL_UNLOADED_EVENT, onUnload);
    };

    const onLoad = (e) => {
      if (e.model.loader !== this) {
        return;
      }

      clearListeners();
      resolve(true);
    };

    const onUnload = (e) => {
      if (e.model.loader !== this) {
        return;
      }

      clearListeners();
      resolve(false);
    };

    // Use the following method.
    // av.waitForCompleteLoad(viewer, options)
    viewer.addEventListener(av.MODEL_ROOT_LOADED_EVENT, onLoad);
    viewer.addEventListener(av.MODEL_UNLOADED_EVENT, onUnload);
  });
};

/**
 * Check if on demand loading is done and send event in that case
 * Fires Autodesk.Viewing.FRAGMENTS_LOADED_EVENT if loading is done
 */
OtgLoader.prototype.trackOnDemandLoadProgress = function () {
  if (this.svf.loadDone && this.fragLoadingInProgress === false && this.pendingRequests === 0)
  this.viewer3DImpl.api.dispatchEvent({ type: av.FRAGMENTS_LOADED_EVENT, loader: this, model: this.model });
};

/**
 * Interface called by SelectiveLoadingController
 */
OtgLoader.prototype.evaluate = function (fragmentID) {
  if (this.loadAborted() || this.options.skipMeshLoad) {
    return;
  }
  // This case is used if fragments are evaluated as they come in.
  if (fragmentID !== undefined) {
    this.tryToActivateFragment(fragmentID, 'fragment');
    return;
  }
  // This branch is used if filtering is deferred, i.e. if we need to wait for the property db.
  // TODO: This only works for the initial filter execution at the moment. Filter updates are not supported yet.
  const fragmentList = this.model.getFragmentList();
  const fragmentLength = fragmentList.fragments.length;
  for (let fragmentID = 0; fragmentID < fragmentLength; ++fragmentID) {
    this.tryToActivateFragment(fragmentID, 'fragment');
  }
};

/**
 * Activates given fragments and loads their referenced geometries and materials if necessary.
 * Already activated fragments are skipped.
 *
 * @param {number[]} fragIds - IDs of fragments to load
 * @param {MeshFlags} loadedFlag - A flag that will be unset on the fragments
 */
OtgLoader.prototype.loadFragments = function (fragIds, loadedFlag) {

  // Reset to get repaints while loading
  // TODO: disabled because the constant repaints eat a lot of CPU and also cause flickering
  // because the edges are overdrawn. We'll need to find something better that doesn't repaint everything all the time.
  // In the meantime, we'll need to keep the camera moving to see geometry loading progress.
  // this.svf.nextRepaintPolys = 0;
  // this.svf.numRepaints = 0;

  const fl = this.model.getFragmentList();

  this.fragLoadingInProgress = true;
  const fragsLoaded = fl.load(fragIds, loadedFlag);
  for (const fragId of fragsLoaded) {
    this.tryToActivateFragment(fragId, 'fragment');
  }
  this.fragLoadingInProgress = false;

  // trigger load event in case no materials or geometries need to be fetched
  if (fragsLoaded.length > 0)
  this.trackOnDemandLoadProgress();
};

/**
 * Unloads given fragments and their geometry if not needed anymore
 * Fires Autodesk.Viewing.FRAGMENTS_UNLOADED_EVENT when all fragments are unloaded
 *
 * @private
 * @param {Iterable.<number>} fragIds - IDs of fragments to unload
 * @param {MeshFlags} loadedFlag - A flag that will be set on the fragments
 */
OtgLoader.prototype.unloadFragments = function (fragIds, loadedFlag) {

  const fl = this.model.getFragmentList();

  const fragsUnloaded = fl.unload(fragIds, loadedFlag);

  if (fragsUnloaded.length > 0) {
    const geomCache = this.viewer3DImpl.geomCache(/* create */false);
    for (const fragId of fragsUnloaded) {
      fl.geoms.removeInstance(fl.geomids[fragId], geomCache);
    }
    this.svf.fragsLoaded -= fragsUnloaded.length;
    this.viewer3DImpl.api.dispatchEvent({ type: av.FRAGMENTS_UNLOADED_EVENT, model: this.model });
  }
};

/**
 * Used by out-of-core tilemanager to detect that the LoadGeometryTask can be used with this loader.
 * The flag is a hint that this loader implements the IGeometryLoader interface, which is required by GeometryLoadTask (see LoadGeometryTask.js).
 */
OtgLoader.prototype.supportsGeometryLoadTask = true;

// Provide remaining functions required by IGeometryLoader interface, so that GeometryLoadTask can use it.
OtgLoader.prototype.getGeometryHash = function (geomId) {return this.svf.getGeometryHash(geomId);};
OtgLoader.prototype.getMaterialId = function (fragId) {return this.svf.fragments.materials[fragId];};
OtgLoader.prototype.getMaterialHash = function (materialId) {return this.svf.getMaterialHash(materialId);};
OtgLoader.prototype.isMaterialPending = function (hash) {return this.pendingMaterials.has(hash);};
OtgLoader.prototype.geomCache = function () {return this.viewer3DImpl.geomCache();};

FileLoaderManager.registerFileLoader("json", ["json"], OtgLoader);